import { hasError } from '@repo/shared/api/helpers'
import { DocumentType as DocumentEnum } from '@repo/shared/dtos/document'
import { isNoteDocument } from '@repo/shared/utils'
import { Card } from '@repo/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@repo/ui/dropdownMenu'
import { useT } from '@repo/ui/hooks'
import {
  DownloadIcon,
  EllipsisVertical,
  FileImageIcon,
  FilePenLineIcon,
  FileTextIcon,
  InfoIcon,
  Loader2,
  Pen,
  Trash2,
} from 'lucide-react'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { DocumentFileEditor, NoteEditor } from '@/components/beneficiary/DocumentEditor'
import { ClampedContent } from '@/components/ClampedContent'
import { ItemCreatedDate, ItemUpdatedDate } from '@/components/CollapsibleCard'
import { api } from '@/main'
import { AvailableParams } from '@/routes/router'
import { storeActions } from '@/store/actions'
import { Document as DocumentType, DocumentFile } from '@/store/document'
import { useStoreDispatch } from '@/store/hooks'

export function Document({
  contentComp,
  document,
}: {
  contentComp?: ReactNode
  document: DocumentType
}) {
  const [open, setOpen] = useState(false)
  const isNote = isNoteDocument(document)
  const content = isNote ? document.content : document.title
  return (
    <div key={document.id} className="flex items-start gap-1">
      <Card
        variant="passive"
        className="hover:border-primary-200 w-full shadow-none transition-colors"
      >
        <div className="text-primary w-full p-4">
          <Options document={document} onClick={() => setOpen(true)} />
          <ClampedContent
            buttonProps={{
              variant: 'link',
              className: 'w-fit text-primary-500 mt-2',
            }}
            content={content}
            contentComp={contentComp}
            lines={12}
          />
          {open && (
            <>
              {isNote ? (
                <NoteEditor note={document} modalHandler={{ open, setOpen }} />
              ) : (
                <DocumentFileEditor document={document} modalHandler={{ open, setOpen }} />
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  )
}

function Options({ document, onClick }: { document: DocumentType; onClick: () => void }) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()

  const handleDelete = () => {
    void dispatch(storeActions.beneficiary.document.delete({ document }))
  }
  const isImage = document.type === DocumentEnum.IMAGE
  const isNote = isNoteDocument(document)

  return (
    <>
      <DropdownMenu>
        <div className="mb-3 flex w-full items-center justify-between">
          <div className="text-primary-400 flex select-none items-center gap-1.5">
            {isImage ? (
              <FileImageIcon className="size-4" />
            ) : isNote ? (
              <FilePenLineIcon className="size-4" />
            ) : (
              <FileTextIcon className="size-4" />
            )}
            <span className="text-xs">{tc(`documentType-${document.type}`, { cfl: true })}</span>
          </div>

          <DropdownMenuTrigger asChild>
            <div className="flex cursor-pointer select-none items-center gap-1 underline-offset-2 hover:underline data-[state=open]:underline">
              <ItemUpdatedDate className="text-xs" updatedAt={document.updatedAt} />
              <EllipsisVertical className="size-4" />
            </div>
          </DropdownMenuTrigger>
        </div>
        <DropdownMenuContent side="bottom" className="min-w-44">
          <DropdownMenuGroup>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <InfoIcon className="size-4" /> {tc('details', { cfl: true })}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuLabel className="font-normal">
                  <ItemCreatedDate
                    className="text-inherit"
                    labelClassName="font-medium"
                    createdAt={document.createdAt}
                  />
                </DropdownMenuLabel>

                <DropdownMenuSeparator />

                <DropdownMenuLabel className="font-normal">
                  <span className="font-medium">{tc('type', { cfl: true, colon: true })} </span>
                  <span className="uppercase">{document.type}</span>
                </DropdownMenuLabel>

                {'fileName' in document && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="max-w-60 select-text whitespace-break-spaces text-pretty break-all font-normal">
                      <span className="font-medium">
                        {tc('fileName', { cfl: true, colon: true })}
                      </span>
                      <br />
                      {document.fileName}
                    </DropdownMenuLabel>
                  </>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuGroup>

          <DropdownMenuGroup>
            <DropdownMenuSeparator />

            {!isNote && <DownloadFile file={document} />}

            <DropdownMenuItem onClick={onClick}>
              <Pen className="size-4" />
              {tc('edit', { cfl: true })}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              className="text-red-500 focus:bg-red-100 focus:text-red-500"
              onClick={handleDelete}
              isSensitive
            >
              <Trash2 className="size-4" />
              {tc('delete', { cfl: true })}
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

function DownloadFile({ file }: { file: DocumentFile }) {
  const { beneficiaryId, organizationId } = useParams<AvailableParams>()
  const { tc } = useT()
  if (!beneficiaryId || !organizationId) return null

  const [url, setUrl] = useState<string | null>(null)

  const getUrl = useCallback(async () => {
    if (url) return
    const res = await api.documents.getTemporaryUrl({
      organizationId,
      beneficiaryId,
      documentId: file.id,
    })
    if (hasError(res)) return
    setUrl(res.url)
  }, [url, organizationId, beneficiaryId, file.id])

  useEffect(() => {
    // FIXME: wrong to use async function in useEffect
    void getUrl()
    return () => {
      setUrl(null)
    }
  }, [])

  if (url) {
    return (
      <>
        <DropdownMenuItem asChild>
          <a href={url} target="_blank" download={file.fileName}>
            <DownloadIcon className="size-4" />
            {tc('download', { cfl: true })}
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
      </>
    )
  }

  return (
    <>
      <DropdownMenuItem disabled>
        <Loader2 className="size-4 animate-spin" />
        {tc('loading', { cfl: true })}...
      </DropdownMenuItem>
      <DropdownMenuSeparator />
    </>
  )
}
